import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps, ReactNode } from 'react';
import { useState } from 'react';
import type { To } from 'react-router';
import styled, { type CSSProperties } from 'styled-components';

import {
  DivisionChange,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS } from '@sorare/core/src/atoms/typography';
import { LineupCardsLayout } from '@sorare/core/src/components/lineup/LineupCardsLayout';
import { ContenderLayout } from '@sorare/core/src/components/lobby/ContenderLayout';
import type { LineupStyle } from '@sorare/core/src/components/lobby/ContenderLayout/types';
import { Variant } from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { RewardMultiplier } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardMultiplier';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { range } from '@sorare/core/src/lib/arrays';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { ContenderDrawer } from '#src/components/contender/ContenderDrawer/index.tsx';
import { ContenderDrawerTabs } from '#src/components/contender/ContenderDrawer/types.ts';
import { LineupBezelWrapper } from '#src/components/lineup/LineupBezelWrapper/index.tsx';
import { SimpleCardPlaceholder } from '#src/components/lineup/SimpleCardPlaceholder/index.tsx';
import { So5Lineup } from '#src/components/lineup/So5Lineup/index.tsx';
import { usePreferredLineupStyle } from '#src/hooks/usePreferredLineupStyle.ts';
import { LineupLayoutHeader } from '#src/layouts/LineupLayout/LineupLayoutHeader/index.tsx';
import { SimpleContenderFooterStatus } from '#src/layouts/LineupLayout/LiveLineupFooter/LeftLiveLineupFooter/SimpleContenderFooterStatus/index.tsx';
import { isFixtureOpened } from '#src/lib/so5.ts';

import { ContenderFooter } from './ContenderFooter/index.tsx';
import { ContenderLiveProjectedRanking } from './ContenderLiveProjectedRanking/index.tsx';
import { DivisionChangeBanner } from './DivisionChangeBanner/index.tsx';
import { TargetBanner } from './TargetBanner/index.tsx';
import type { Contender_so5LeaderboardContender } from './__generated__/index.graphql.ts';

const divisionChangeColor: Record<DivisionChange, string> = {
  [DivisionChange.PROMOTED]: 'var(--c-rgb-onBase-success)',
  [DivisionChange.RELEGATED]: 'var(--c-rgb-onBase-error)',
  [DivisionChange.STABLE]: '200, 200, 200',
};
const pveTaskStatusColor: Record<'success' | 'failed' | 'in_progress', string> =
  {
    success: 'var(--c-rgb-onBase-success)',
    failed: 'var(--c-rgb-onBase-error)',
    in_progress: '200, 200, 200',
  };

const getColors = ({
  divisionChange,
  pveTaskStatus,
  withBoostedRewards,
}: {
  divisionChange?: DivisionChange;
  pveTaskStatus?: 'success' | 'failed' | 'in_progress';
  withBoostedRewards: boolean;
}) => {
  if (divisionChange) {
    return divisionChangeColor[divisionChange];
  }
  if (pveTaskStatus) {
    return pveTaskStatusColor[pveTaskStatus];
  }
  if (withBoostedRewards) {
    return '252, 255, 99';
  }
  return undefined;
};

const Anchor = styled.div`
  height: 100%;

  /**
  * --current-stack-height doesn't work here because the hash scroll happens
  * before the measure has time to finish and updating scroll-margin-top value
  * after the scroll happened is a no-op
  */
  max-width: 100%;
  scroll-margin-top: calc(var(--app-header-height-mobile) + 60px + 44px);
  @media ${desktopAndAbove} {
    scroll-margin-top: calc(var(--app-header-height-desktop) + 88px + 44px);
  }
`;

const SlotFooter = styled.div`
  display: grid;
  grid-template-rows: var(--triple-unit) var(--double-and-a-half-unit);
  gap: var(--half-unit);
  justify-items: center;
`;

const OPTIMISTIC_SLOTS_COUNT_PER_SPORT: Record<Sport, number> = {
  [Sport.FOOTBALL]: 5,
  [Sport.NBA]: 5,
  [Sport.BASEBALL]: 7,
};
const Separator = styled.div`
  &:first-child,
  &:last-child {
    display: none;
  }
  width: 1px;
  height: var(--double-unit);
  background-color: var(--c-nd-150);
`;

const HorizontalHiddenIfEmpty = styled(Horizontal).attrs({
  gap: 0.5,
  as: BrandXXS,
})`
  color: var(--c-nd-600);
  &:empty {
    display: none;
  }
`;

type Props = {
  anchorId?: string;
  so5LeaderboardContender: Nullable<Contender_so5LeaderboardContender>;
  to?: To;
  onClick?: () => void;
  lineupStyle?: LineupStyle;
  skeletonOptions?: {
    withButtons?: boolean;
    displayBanner?: boolean;
  };
  customFooter?: ReactNode;
  hideSubFooter?: boolean;
  onLineupUpdated?: () => void;
  onSwap?: ComponentProps<typeof So5Lineup>['onSwap'];
  swappingSlots?: ComponentProps<typeof So5Lineup>['swappingSlots'];
  draggedPayload?: ComponentProps<typeof So5Lineup>['draggedPayload'];
  setDraggedPayload?: ComponentProps<typeof So5Lineup>['setDraggedPayload'];
} & Pick<ComponentProps<typeof So5Lineup>, 'fillHeightPercent'> &
  Pick<ComponentProps<typeof ContenderDrawer>, 'onClickGame'> &
  Pick<ComponentProps<typeof LineupLayoutHeader>, 'onLineupUpdated'>;

export const Contender = ({
  anchorId,
  so5LeaderboardContender,
  to,
  onClick,
  lineupStyle: lineupStyleFromProps,
  fillHeightPercent,
  skeletonOptions,
  onClickGame,
  customFooter,
  hideSubFooter,
  onLineupUpdated,
  onSwap,
  swappingSlots,
  draggedPayload,
  setDraggedPayload,
}: Props) => {
  const { sport } = useSportContext();
  const { isMe } = useCurrentUserContext();

  const [drawerContender, setDrawerContender] = useState<string>();
  const [drawerTab, setDrawerTab] = useState<ContenderDrawerTabs>();
  const [lineupStyle] = usePreferredLineupStyle();
  const lineupStyleToApply = lineupStyleFromProps || lineupStyle;

  if (!so5LeaderboardContender) {
    const slotsCount = OPTIMISTIC_SLOTS_COUNT_PER_SPORT[sport];

    return (
      <Anchor id={anchorId}>
        <LineupBezelWrapper>
          <ContenderLayout
            lineupStyle={lineupStyleToApply}
            withButtons={skeletonOptions?.withButtons}
            header={
              <Horizontal>
                <Skeleton
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                  }}
                />
                <Vertical gap={0.5}>
                  <Skeleton
                    style={{
                      width: '90px',
                      height: '14px',
                      borderRadius: '8px',
                    }}
                  />
                  <Skeleton
                    style={{
                      width: '130px',
                      height: '14px',
                      borderRadius: '8px',
                    }}
                  />
                </Vertical>
              </Horizontal>
            }
            banner={!!skeletonOptions?.displayBanner && <div />}
            footer={
              <Skeleton
                style={{
                  width: '100%',
                  height: skeletonOptions?.withButtons ? '40px' : '100%',
                }}
              />
            }
            subFooter={null}
          >
            <LineupCardsLayout
              fillHeightPercent={fillHeightPercent}
              lineupStyle={lineupStyleToApply}
              slots={slotsCount}
            >
              {range(slotsCount).map(i => (
                <Vertical key={i}>
                  <SimpleCardPlaceholder
                    icon={null}
                    label=""
                    loading={!so5LeaderboardContender}
                  />
                  <SlotFooter>
                    <Skeleton
                      style={{
                        width: '32px',
                        height: '24px',
                        borderRadius: '3em',
                      }}
                    />
                    <Skeleton
                      style={{
                        width: '48px',
                        height: '20px',
                        borderRadius: '8px',
                      }}
                    />
                  </SlotFooter>
                </Vertical>
              ))}
            </LineupCardsLayout>
          </ContenderLayout>
        </LineupBezelWrapper>
      </Anchor>
    );
  }

  const { so5Leaderboard, so5Lineup } = so5LeaderboardContender;
  const { stadiumUrl, so5LeaderboardGroupByCompetition } = so5Leaderboard;
  const { so5Rankings } = so5Lineup || {};

  const ranking = so5Rankings?.at(0);

  const showProjectedRankingSubFooter = !!ranking?.liveProjectedRanking;
  const showRewardsSubFooter = !!so5Lineup && so5Lineup.rewardMultiplier > 1;

  const getPveLineupStatus = () => {
    if (so5Leaderboard.pveOnly) {
      if (so5Lineup?.completedTasks?.at(0)) {
        return 'success';
      }
      if (
        so5Lineup?.scheduledSo5AppearancesCount === 0 &&
        !so5Lineup?.hasLiveGames
      ) {
        return 'failed';
      }
      return 'in_progress';
    }
    return undefined;
  };

  const pveTaskStatus = getPveLineupStatus();

  const color = getColors({
    divisionChange: ranking?.eligibleDivisionChange || undefined,
    pveTaskStatus,
    withBoostedRewards: so5Leaderboard.roomWithBoostedRewards,
  });

  const content = (
    <>
      <Anchor
        id={anchorId}
        style={{ '--footer-height': 'auto' } as CSSProperties}
      >
        <LineupBezelWrapper
          color={color}
          lineupStyle={lineupStyleToApply}
          backgroundUrl={
            (lineupStyleToApply === 'spread'
              ? stadiumUrl
              : so5LeaderboardGroupByCompetition.tileBackgroundUrl) || ''
          }
        >
          <ContenderLayout
            lineupStyle={lineupStyleToApply}
            withButtons={isFixtureOpened(so5Leaderboard.so5Fixture)}
            withSubFooter={
              showProjectedRankingSubFooter || showRewardsSubFooter
            }
            header={
              <LineupLayoutHeader
                small
                so5LeaderboardContender={so5LeaderboardContender}
                to={to}
                disableLineupViewSelector={!!lineupStyleFromProps}
                onClick={onClick}
                onLineupUpdated={onLineupUpdated}
              />
            }
            banner={
              <>
                {!!ranking?.eligibleDivisionChange && (
                  <DivisionChangeBanner
                    divisionChange={ranking.eligibleDivisionChange}
                  />
                )}
                {!ranking?.eligibleDivisionChange &&
                  !!so5Leaderboard.pveOnly && (
                    <TargetBanner targetStatus={getPveLineupStatus()} />
                  )}
              </>
            }
            footer={
              <ContenderFooter
                showButtons={
                  !so5LeaderboardContender.so5Lineup ||
                  isMe(so5LeaderboardContender.so5Lineup?.user)
                }
                so5LeaderboardContender={so5LeaderboardContender}
                onClickPrizePool={() => {
                  setDrawerTab(ContenderDrawerTabs.PRIZE_POOL);
                  setDrawerContender(so5LeaderboardContender.slug);
                }}
                onClickBonus={() => {
                  setDrawerTab(ContenderDrawerTabs.BONUS);
                  setDrawerContender(so5LeaderboardContender.slug);
                }}
                customFooter={customFooter}
              />
            }
            subFooter={
              !hideSubFooter &&
              !so5Leaderboard.pveOnly && (
                <HorizontalHiddenIfEmpty gap={1} center>
                  {!!showProjectedRankingSubFooter && (
                    <ContenderLiveProjectedRanking so5Ranking={ranking} />
                  )}
                  <SimpleContenderFooterStatus
                    so5LeaderboardContender={so5LeaderboardContender}
                    variant="SECONDARY"
                  />
                  {!!showRewardsSubFooter && (
                    <>
                      <Separator />
                      <RewardMultiplier
                        lineup={so5Lineup}
                        variant={Variant.TEXT}
                      />
                    </>
                  )}
                </HorizontalHiddenIfEmpty>
              )
            }
          >
            <So5Lineup
              fillHeightPercent={fillHeightPercent}
              so5LeaderboardContender={so5LeaderboardContender}
              lineupStyle={lineupStyleToApply}
              onSwap={onSwap}
              swappingSlots={swappingSlots}
              draggedPayload={draggedPayload}
              setDraggedPayload={setDraggedPayload}
            />
          </ContenderLayout>
        </LineupBezelWrapper>
      </Anchor>
      {!!drawerContender &&
        drawerContender === so5LeaderboardContender.slug &&
        !!drawerTab && (
          <ContenderDrawer
            open
            onClose={() => {
              setDrawerContender(undefined);
              setDrawerTab(undefined);
            }}
            tab={drawerTab}
            onChangeTab={setDrawerTab}
            contender={drawerContender}
            onClickGame={onClickGame}
          />
        )}
    </>
  );

  if (so5Leaderboard?.pveOnly) {
    return (
      <div style={{ '--custom-footer-height': 'auto' } as CSSProperties}>
        {content}
      </div>
    );
  }

  return content;
};

Contender.fragments = {
  so5LeaderboardContender: gql`
    fragment Contender_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        slug
        stadiumUrl
        pveOnly
        roomWithBoostedRewards
        so5Fixture {
          slug
          ...isFixtureOpened_so5Fixture
        }
        so5LeaderboardGroupByCompetition: so5LeaderboardGroup(
          groupType: COMPETITION
        ) {
          slug
          tileBackgroundUrl
        }
      }
      so5Lineup {
        id
        hasLiveGames
        scheduledSo5AppearancesCount
        rewardMultiplier
        so5Rankings {
          id
          eligibleDivisionChange
          liveProjectedRanking
          ...ContenderLiveFooter_so5Ranking
        }
        user {
          slug
        }
        ...RewardMultiplier_lineup
      }
      ...So5Lineup_so5LeaderboardContender
      ...LineupLayoutHeader_so5LeaderboardContender
      ...ContenderFooter_so5LeaderboardContender
      ...SimpleContenderFooterStatus_so5LeaderboardContender
    }
    ${So5Lineup.fragments.so5LeaderboardContender}
    ${LineupLayoutHeader.fragments.so5LeaderboardContender}
    ${ContenderFooter.fragments.so5LeaderboardContender}
    ${isFixtureOpened.fragments.so5Fixture}
    ${ContenderLiveProjectedRanking.fragments.so5Ranking}
    ${RewardMultiplier.fragments.lineup}
    ${SimpleContenderFooterStatus.fragments.so5LeaderboardContender}
  ` as TypedDocumentNode<Contender_so5LeaderboardContender>,
};
